import { useState, useEffect, useRef } from 'react';

export default function GestaoPortfolios({ items = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const intervalRef = useRef(null);
  const carouselRef = useRef(null);
  const containerRef = useRef(null);
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Use provided items or default items
  const carouselItems = items;

  // Touch functionality for mobile
  const handleTouchStart = (e) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };
  
  const handleTouchEnd = () => {
    if (isTransitioning) return;
    
    if (touchStart.x && touchEnd.x) {
      const xDifference = touchStart.x - touchEnd.x;
      const yDifference = touchStart.y - touchEnd.y;
      
      // Only process horizontal swipes (if horizontal movement is greater than vertical)
      if (Math.abs(xDifference) > Math.abs(yDifference) && Math.abs(xDifference) > 30) {
        if (xDifference > 0) {
          // Swipe left - go to next slide
          if (currentIndex < carouselItems.length - 1) {
            goToNext();
            resetInterval();
          }
        } else {
          // Swipe right - go to previous slide
          if (currentIndex > 0) {
            goPrev();
            resetInterval();
          }
        }
      } else if (Math.abs(yDifference) > Math.abs(xDifference) && Math.abs(yDifference) > 50) {
        // Process vertical swipes as fallback
        if (yDifference > 0) {
          // Swipe up - go to next slide
          if (currentIndex < carouselItems.length - 1) {
            goToNext();
            resetInterval();
          }
        } else {
          // Swipe down - go to previous slide
          if (currentIndex > 0) {
            goPrev();
            resetInterval();
          }
        }
      }
    }
    
    // Reset values
    setTouchStart({ x: 0, y: 0 });
    setTouchEnd({ x: 0, y: 0 });
  };
  
  // Mouse wheel scrolling functionality
  useEffect(() => {
    const handleWheel = (e) => {
      if (isTransitioning) return;
      
      if (e.deltaY > 0) {
        // Scrolling down
        if (currentIndex < carouselItems.length - 1) {
          // If not at the last slide, move to next slide and prevent default scrolling
          e.preventDefault();
          goToNext();
        }
        // If at the last slide, allow natural page scrolling (don't prevent default)
      } else if (e.deltaY < 0) {
        // Scrolling up
        if (currentIndex > 0) {
          // If not at the first slide, move to previous slide and prevent default scrolling
          e.preventDefault();
          goPrev();
        }
        // If at the first slide, allow natural page scrolling (don't prevent default)
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isTransitioning, currentIndex, carouselItems.length]);
  
  // Auto-scroll functionality
  useEffect(() => {
    // Start autoplay when component mounts
    intervalRef.current = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselItems.length);
        setTimeout(() => setIsTransitioning(false), 500);
      }
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // Empty dependency array to ensure it only runs once on mount

  const goToNext = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselItems.length);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const goPrev = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? carouselItems.length - 1 : prevIndex - 1
      );
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const goToSlide = (index) => {
    if (!isTransitioning && index !== currentIndex) {
      setIsTransitioning(true);
      setCurrentIndex(index);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  // Reset interval when manually changing slides
  const resetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        goToNext();
      }, 5000);
    }
  };

  return (
    <div className="bg-[#040116] text-white bg-[url(../assets/cover-proposito.png.png)] bg-no-repeat bg-cover relative overflow-hidden">
      <div 
        ref={containerRef} 
        className="container mx-auto w-full max-w-[1048px] py-8 md:py-24 relative"
      >
        {/* Timeline dot on the left - desktop only */}
        {!isMobile && (
          <div className="absolute top-[80px] bottom-[80px] w-[3px] bg-white flex flex-col items-center">
            {/* Dashed circle around the dot */}
            <div 
              className="w-14 h-14 rounded-full border-dashed border-2 border-white absolute left-1/2 -translate-x-1/2 transition-all duration-500 flex items-center justify-center" 
              style={{ 
                top: carouselItems.length <= 1 ? '50%' : `calc(${(currentIndex / (carouselItems.length - 1)) * 100}%)`,
                transform: 'translateY(-50%)'
              }}
            >
              {/* Solid white dot */}
              <div className="w-4 h-4 rounded-full bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)]"></div>
            </div>
          </div>
        )}

        {/* Carousel content */}
        <div className="flex flex-row justify-between items-center relative z-10">
          <div 
            className={`w-full ${isMobile ? 'px-4 pr-12' : 'pl-32'} overflow-hidden relative`} 
            ref={carouselRef} 
            style={{ height: isMobile ? '350px' : '300px' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {carouselItems.map((item, index) => (
              <div 
                key={index} 
                className={`py-10 ${isMobile ? 'px-12' : 'px-0'} flex flex-col justify-center items-center text-center transition-all duration-500 absolute top-0 left-0 w-full h-full ${currentIndex === index ? 'opacity-100 z-10' : 'opacity-0 -z-10'}`}
              >
                <h2 className={`font-normal ${isMobile ? 'text-2xl' : 'text-4xl'} group relative inline-block mb-${isMobile ? '10' : '16'}`}>
                  <span className="">
                    {item.title}
                    <span className="absolute left-0 bottom-[-3px] h-[2px] w-full bg-gradient-to-r from-[#4B3BDB] via-[#A002CB] to-[#FC0021] transition-all duration-200" />
                  </span>
                </h2>
                <p className={`font-inter-regular ${isMobile ? 'text-lg' : 'text-3xl'} max-w-3xl text-center`}>
                  {item.content}
                </p>
              </div>
            ))}
          </div>

          {/* Indicators on the right */}
          <div className={`flex flex-col gap-${isMobile ? '2.5' : '4'} items-center absolute right-${isMobile ? '4' : '0'} top-1/2 transform -translate-y-1/2 z-20`}>
            {carouselItems.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  goToSlide(index);
                  resetInterval();
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer hover:scale-110 hover:opacity-80 ${
                  currentIndex === index 
                    ? `${isMobile ? 'w-2 h-4' : 'w-2 h-6'} bg-transparent border-2 border-white` 
                    : 'bg-white shadow-[0_0_5px_rgba(255,255,255,0.5)]'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
